import { type KeyboardEvent, useRef } from 'react';

import { cn } from '@shared/lib/cn';
import { formatNumber, formatPercent } from '@shared/lib/format';

import {
  CHART_DEFAULT_WIDTH,
  CHART_SIZE_CLASS,
  SERIES_FILL_CLASS,
  STACKED_BAR_LAYOUT as L,
} from './chart.constant';
import { crisp } from './chart.lib';
import { CHART_TEXT } from './chart.text';
import { useElementWidth } from './chart-size.hook';
import { useChartTooltip } from './chart-tooltip.hook';
import { ChartTooltip } from './chart-tooltip.ui';
import type { ChartTooltipContent } from './model/chart-tooltip.types';
import type { StackedBarGroup } from './model/stacked-bar.types';
import { stackedBarScale, stackedSegments } from './stacked-bar.lib';

export interface StackedBarChartProps {
  groups: StackedBarGroup[];
  yTicks?: number[];
  formatTotal?: (total: number) => string;
  formatTick?: (tick: number) => string;
  tooltip?: (group: StackedBarGroup, index: number) => ChartTooltipContent;
  onGroupSelect?: (group: StackedBarGroup, index: number) => void;
  className?: string;
}

const HEIGHT = L.padding * 2 + L.labelHeight + L.plotHeight + L.valueHeight;
const PLOT_TOP = L.padding + L.labelHeight;
const PLOT_BOTTOM = PLOT_TOP + L.plotHeight;
const KEY = 'bar-';

const defaultFormat = (value: number) => formatNumber(value);

/** 말풍선 기본 내용: 제목 = 막대 이름, 줄 = 조각(위에 쌓인 것부터) 색 · 이름 · 비율 */
const defaultTooltip = (group: StackedBarGroup): ChartTooltipContent => ({
  title: group.label,
  rows: [...group.segments].reverse().map((segment) => ({
    color: { kind: 'series', step: segment.series },
    name: segment.label,
    value: formatPercent(segment.value, { maximumFractionDigits: 1 }),
  })),
});

/**
 * 라운드별 누적 막대(막대 높이 = 합계에 비례, 조각 = 항목별 비율). 직접 그린 SVG(DD-54 #1).
 * 높이 388 = 바깥 여백 20 + 막대 이름 줄 24 + 막대 영역 300 + 값 줄 24 + 바깥 여백 20, 너비는 놓이는 자리를 채운다.
 * 세로축 눈금 5개 + 가로 격자선 + 바닥선. 조각은 계열 색 60%(올린 막대 80% · 1.02 배), 뒤에 막대 바탕(`dataviz/track`).
 * 막대 이름 = 축 글자(막대 위), 합계 = 값 글자(막대 아래). 올리면 ChartTooltip — 그래프 가운데보다 왼쪽 막대는 오른쪽에, 오른쪽 막대는 왼쪽에.
 */
export function StackedBarChart({
  groups,
  yTicks,
  formatTotal = defaultFormat,
  formatTick = defaultFormat,
  tooltip: tooltipOf = defaultTooltip,
  onGroupSelect,
  className,
}: StackedBarChartProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const width = useElementWidth(rootRef, CHART_DEFAULT_WIDTH);
  const { tooltip, show, hide, hideNow, bubbleProps } = useChartTooltip();

  const scale = stackedBarScale(groups, yTicks);
  const plotLeft = L.padding + L.axisWidth;
  const plotWidth = Math.max(0, width - plotLeft - L.padding);
  const column = groups.length === 0 ? plotWidth : plotWidth / groups.length;
  const barWidth = Math.max(4, Math.min(L.barMaxWidth, column - L.barGap));
  const yOf = (value: number) => PLOT_BOTTOM - (value / scale.max) * L.plotHeight;
  const interactive = onGroupSelect !== undefined;

  const activeIndex =
    tooltip?.key.startsWith(KEY) === true ? Number(tooltip.key.slice(KEY.length)) : null;

  const showGroup = (index: number) => {
    const group = groups[index];
    const rect = svgRef.current?.getBoundingClientRect();
    if (!group || !rect) return;
    const barLeft = plotLeft + column * index + (column - barWidth) / 2;
    const toRight = barLeft + barWidth / 2 < width / 2;
    show({
      key: `${KEY}${index}`,
      content: tooltipOf(group, index),
      anchor: {
        x: rect.left + (toRight ? barLeft + barWidth : barLeft),
        y: rect.top + (yOf(Math.max(0, group.total)) + PLOT_BOTTOM) / 2,
      },
      placement: toRight ? 'right' : 'left',
    });
  };

  const onBarKeyDown = (event: KeyboardEvent<SVGGElement>, index: number) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onGroupSelect?.(groups[index]!, index);
  };

  return (
    <div
      ref={rootRef}
      data-chart="stacked-bar"
      className={cn('relative w-full bg-bg-surface', CHART_SIZE_CLASS.stackedBar, className)}
    >
      <svg
        ref={svgRef}
        role={interactive ? 'group' : 'img'}
        aria-label={CHART_TEXT.summary.stackedBar(groups.map((group) => group.label))}
        width={width}
        height={HEIGHT}
        className="block select-none"
      >
        {/* 가로 격자선 · 바닥선 · 세로축 눈금 */}
        {scale.ticks.map((tick, index) => {
          const y = crisp(yOf(tick));
          return (
            <g key={`${index}-${tick}`}>
              <line
                data-slot={tick === 0 ? 'baseline' : 'grid'}
                x1={plotLeft}
                x2={plotLeft + plotWidth}
                y1={y}
                y2={y}
                strokeWidth={1}
                shapeRendering="crispEdges"
                className={tick === 0 ? 'stroke-dataviz-baseline' : 'stroke-dataviz-grid'}
              />
              <text
                aria-hidden
                x={plotLeft - 8}
                y={y}
                textAnchor="end"
                dominantBaseline="central"
                className="fill-dataviz-axis typo-caption"
              >
                {formatTick(tick)}
              </text>
            </g>
          );
        })}

        {groups.map((group, index) => {
          const barLeft = Math.round(plotLeft + column * index + (column - barWidth) / 2);
          const total = Number.isFinite(group.total) ? Math.max(0, group.total) : 0;
          const barTop = yOf(total);
          const barHeight = PLOT_BOTTOM - barTop;
          const active = activeIndex === index;
          const totalText = formatTotal(group.total);
          return (
            <g
              // 같은 이름의 막대(구 "현재" 두 개)가 서로 섞이지 않게 순서를 키에 넣는다
              key={`${index}-${group.label}`}
              data-slot="group"
              data-active={active || undefined}
              role={interactive ? 'button' : undefined}
              tabIndex={interactive ? 0 : undefined}
              aria-label={interactive ? `${group.label} ${totalText}` : undefined}
              onPointerEnter={() => showGroup(index)}
              onPointerLeave={hide}
              onFocus={() => showGroup(index)}
              onBlur={hideNow}
              onClick={interactive ? () => onGroupSelect(group, index) : undefined}
              onKeyDown={interactive ? (event) => onBarKeyDown(event, index) : undefined}
              className={cn('outline-none', interactive && 'cursor-pointer')}
            >
              {/* 올림을 받는 투명 영역: 막대 칸 전체 */}
              <rect
                x={plotLeft + column * index}
                y={L.padding}
                width={column}
                height={HEIGHT - L.padding * 2}
                fill="transparent"
              />
              <g
                style={{ transformBox: 'fill-box', transformOrigin: 'center bottom' }}
                className={cn('transition-transform duration-200', active && 'scale-102')}
              >
                <rect
                  data-slot="track"
                  x={barLeft}
                  y={barTop}
                  width={barWidth}
                  height={barHeight}
                  shapeRendering="crispEdges"
                  className="fill-dataviz-track"
                />
                {stackedSegments(group).map((placed, segmentIndex) => {
                  const segment = group.segments[segmentIndex]!;
                  return placed.size > 0 ? (
                    <rect
                      key={`${segmentIndex}-${segment.label}`}
                      data-slot="segment"
                      x={barLeft}
                      y={PLOT_BOTTOM - (placed.start + placed.size) * barHeight}
                      width={barWidth}
                      height={placed.size * barHeight}
                      shapeRendering="crispEdges"
                      className={cn(
                        'transition-opacity duration-200',
                        SERIES_FILL_CLASS[segment.series],
                        active ? 'opacity-80' : 'opacity-muted',
                      )}
                    />
                  ) : null;
                })}
              </g>
              <text
                data-slot="label"
                x={barLeft + barWidth / 2}
                y={barTop - L.textGap}
                textAnchor="middle"
                className="fill-dataviz-axis typo-caption"
              >
                {group.label}
              </text>
              <text
                data-slot="total"
                x={barLeft + barWidth / 2}
                y={PLOT_BOTTOM + L.valueHeight / 2}
                textAnchor="middle"
                dominantBaseline="central"
                className="fill-dataviz-value typo-caption"
              >
                {totalText}
              </text>
            </g>
          );
        })}
      </svg>
      {tooltip ? (
        <ChartTooltip
          {...tooltip.content}
          anchor={tooltip.anchor}
          placement={tooltip.placement}
          {...bubbleProps}
        />
      ) : null}
    </div>
  );
}
